import { Liability, RealEstateOwned } from '../../models/mortgage.model';
import { MortgageApplication } from '../../models/mortgage-app.model';
import { activeOnly, isDeleted, removeOrMarkDeletedAll, undoDelete } from '../../utils/entity-state';

/** Liability type that represents a mortgage / lien against a property. */
export const MORTGAGE_LIABILITY_TYPE = 'MortgageLoan';

// Module-scoped decreasing counter so several liabilities created in one
// session get distinct negative placeholder ids (mirrors UtilsService /
// AdminV2 Utils.getUniqueId). The backend treats an id <= 0 as an insert and
// assigns the real id on save; the negative value just keeps Angular track-by
// keys unique for unsaved rows.
let placeholderCounter = 0;
export function nextPlaceholderLiabilityId(): number {
  placeholderCounter += 1;
  const base = (Date.now() & 0xffffffff) + placeholderCounter;
  const positive = base < 0 ? base * -1 : base;
  return positive * -1;
}

/**
 * True only for a mortgage liability this feature owns — i.e. one that carries
 * no real-world identity. A liability from a credit pull, or one a user typed
 * with a holder/account, represents real data we must never overwrite, move,
 * or delete.
 */
export function isFeatureOwned(liability: Liability): boolean {
  return (
    liability.typeOfLiability === MORTGAGE_LIABILITY_TYPE &&
    !liability.fromCreditReport &&
    !liability.holderName &&
    !liability.accountNumber
  );
}

/**
 * Keeps a single mortgage liability in sync with the balance a borrower enters
 * on an REO.
 *
 * Requirement: when a borrower creates or edits an REO and enters a current
 * mortgage balance (reo.amountOfMortgage), there must be a matching
 * MortgageLoan liability linked to that REO — created only if one isn't linked
 * already, and never duplicated when the borrower comes back to the screen.
 *
 * This mirrors the AdminV2 URLA in-memory pattern that the shared
 * `POST api/mortgages/urla2020/{mortgageId}` save already understands:
 *   - a brand-new REO (reoId <= 0) carries its new liability NESTED in
 *     reo.liabilities; on save the backend stamps the REO's real id onto it.
 *   - an existing REO (reoId > 0) carries the liability in
 *     mortgage.liabilities with liability.reoId set.
 * "New" is signalled by an id <= 0. "Removed" is signalled by entityState
 * 'Deleted' on a persisted row (LLS-520: the save only deletes rows that carry
 * that marker, an omitted row is preserved); an unsaved row is just dropped.
 *
 * Safety: this only ever creates / updates / removes a liability THIS feature
 * owns (see isFeatureOwned). If a real (credit-report or manually-entered)
 * mortgage liability is already linked to the REO, the REO's debt is already
 * represented and nothing is touched.
 *
 * Idempotent and safe to call on every open / edit / balance change.
 */
export function syncReoMortgageLiability(mortgage: MortgageApplication, reo: RealEstateOwned): void {
  if (!mortgage || !reo) {
    return;
  }

  mortgage.liabilities = mortgage.liabilities ?? [];
  reo.liabilities = reo.liabilities ?? [];

  // Every MortgageLoan liability already linked to this REO — top-level (by
  // reoId) for an existing REO, or nested for a not-yet-saved one.
  const linkedTopLevel = activeOnly(mortgage.liabilities).filter(
    l => l.typeOfLiability === MORTGAGE_LIABILITY_TYPE && l.reoId != null && l.reoId === reo.reoId
  );
  const linkedNested = activeOnly(reo.liabilities).filter(l => l.typeOfLiability === MORTGAGE_LIABILITY_TYPE);
  const linked = [...linkedTopLevel, ...linkedNested].filter((l, i, arr) => arr.indexOf(l) === i);

  // A real mortgage liability already represents this REO's debt (e.g. a credit
  // 1st + 2nd lien, or a manually linked loan). Never touch the real one(s),
  // but drop any stale auto-created copy so the debt isn't double-counted.
  if (linked.some(l => !isFeatureOwned(l))) {
    const stale = linked.filter(isFeatureOwned);
    if (stale.length) {
      removeOrMarkDeletedAll(mortgage.liabilities, stale, l => l.liabilityId);
      removeOrMarkDeletedAll(reo.liabilities, stale, l => l.liabilityId);
    }
    return;
  }

  const managed = linked; // all remaining are feature-owned
  const hasBalance = !!reo.amountOfMortgage;

  if (!hasBalance) {
    // Balance cleared -> drop every auto-managed liability for this REO.
    if (managed.length) {
      removeOrMarkDeletedAll(mortgage.liabilities, managed, l => l.liabilityId);
      removeOrMarkDeletedAll(reo.liabilities, managed, l => l.liabilityId);
    }
    return;
  }

  // Collapse any stale extra auto-managed copies down to one.
  const extras = managed.slice(1);
  if (extras.length) {
    removeOrMarkDeletedAll(mortgage.liabilities, extras, l => l.liabilityId);
    removeOrMarkDeletedAll(reo.liabilities, extras, l => l.liabilityId);
  }

  const isExistingReo = reo.reoId != null && reo.reoId > 0;
  let liability = managed[0];

  if (!liability) {
    // A balance re-entered after being cleared revives the row we marked deleted,
    // instead of deleting it and inserting a duplicate for the same debt.
    liability = findRevivableLiability(mortgage, reo);
    if (liability) {
      undoDelete(liability);
    }
  }

  if (!liability) {
    liability = new Liability();
    liability.liabilityId = nextPlaceholderLiabilityId();
    liability.typeOfLiability = MORTGAGE_LIABILITY_TYPE;
    // New REO -> nest so the save links it to the REO's new id.
    // Existing REO -> top-level list, keyed by the real reoId.
    (isExistingReo ? mortgage.liabilities : reo.liabilities).push(liability);
  } else if (isExistingReo && reo.liabilities.includes(liability)) {
    // Normalize: an existing REO keeps its liability top-level, not nested.
    // Moving between the two collections is not a delete, so no marker here.
    reo.liabilities = reo.liabilities.filter(l => l !== liability);
    if (!mortgage.liabilities.includes(liability)) {
      mortgage.liabilities.push(liability);
    }
  }

  liability.unpaidBalance = reo.amountOfMortgage;
  liability.monthlyPayment = reo.mortgagePayment;
  liability.reoId = reo.reoId;
  // This liability is feature-owned, so keep its owners aligned with the REO's.
  liability.owningBorrowerIds = resolveOwningBorrowerIds(mortgage, reo);
}

/** A previously auto-managed liability for this REO that is only marked deleted. */
function findRevivableLiability(mortgage: MortgageApplication, reo: RealEstateOwned): Liability | undefined {
  const linkedToReo = (l: Liability) =>
    isDeleted(l) && isFeatureOwned(l) && (reo.liabilities.includes(l) || (l.reoId != null && l.reoId === reo.reoId));
  return mortgage.liabilities.find(linkedToReo) ?? reo.liabilities.find(linkedToReo);
}

/** Owners of the liability follow the REO's owners, falling back to all borrowers. */
function resolveOwningBorrowerIds(mortgage: MortgageApplication, reo: RealEstateOwned): number[] {
  const reoOwners = (reo.owningBorrowerIds ?? []).filter(id => id != null);
  if (reoOwners.length > 0) {
    return reoOwners;
  }
  return (mortgage.borrowers ?? []).map(b => b.borrowerId).filter(id => id != null) as number[];
}
